import { z } from 'zod'

export const VEHICLE_TYPES = [
  'sedan',
  'suv',
  'stretch_limo',
  'party_bus',
  'sprinter',
  'coach',
  'other',
] as const

export const PRICING_TYPES = ['point_to_point', 'hourly'] as const

export const ADDON_PRICE_TYPES = ['flat', 'per_hour', 'per_trip'] as const

export const VEHICLE_TYPE_LABELS: Record<(typeof VEHICLE_TYPES)[number], string> = {
  sedan: 'Sedan',
  suv: 'SUV',
  stretch_limo: 'Stretch Limo',
  party_bus: 'Party Bus',
  sprinter: 'Sprinter',
  coach: 'Coach',
  other: 'Other',
}

export const PRICING_TYPE_LABELS: Record<(typeof PRICING_TYPES)[number], string> = {
  point_to_point: 'Point to Point',
  hourly: 'Hourly',
}

export const ADDON_PRICE_TYPE_LABELS: Record<(typeof ADDON_PRICE_TYPES)[number], string> = {
  flat: 'Flat Fee',
  per_hour: 'Per Hour',
  per_trip: 'Per Trip',
}

// Common features for quick selection
export const COMMON_FEATURES = [
  { name: 'Leather Interior', icon: 'armchair' },
  { name: 'WiFi', icon: 'wifi' },
  { name: 'Bluetooth Audio', icon: 'bluetooth' },
  { name: 'Privacy Partition', icon: 'panel-top-close' },
  { name: 'Mini Bar', icon: 'wine' },
  { name: 'LED Lighting', icon: 'lightbulb' },
  { name: 'Flat Screen TV', icon: 'tv' },
  { name: 'USB Charging Ports', icon: 'usb' },
  { name: 'Climate Control', icon: 'thermometer' },
  { name: 'Complimentary Water', icon: 'glass-water' },
]

// Common add-ons for quick selection
export const COMMON_ADDONS = [
  { name: 'Child Car Seat', price: 15, price_type: 'flat' as const, max_quantity: 4 },
  { name: 'Booster Seat', price: 10, price_type: 'flat' as const, max_quantity: 4 },
  { name: 'Meet & Greet Service', price: 25, price_type: 'flat' as const, max_quantity: 1 },
  { name: 'Extra Stop', price: 15, price_type: 'per_trip' as const, max_quantity: 10 },
  { name: 'Wait Time (per 15 min)', price: 15, price_type: 'flat' as const, max_quantity: 8 },
  { name: 'Red Carpet Service', price: 50, price_type: 'flat' as const, max_quantity: 1 },
  { name: 'Champagne Package', price: 75, price_type: 'flat' as const, max_quantity: 1 },
  { name: 'Flower Arrangement', price: 50, price_type: 'flat' as const, max_quantity: 1 },
]

export const vehicleSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters'),
  slug: z
    .string()
    .min(1, 'Slug is required')
    .regex(/^[a-z0-9-]+$/, 'Slug must be lowercase alphanumeric with dashes'),
  description: z.string().optional().nullable(),
  vehicle_type: z.enum(VEHICLE_TYPES, {
    required_error: 'Vehicle type is required',
  }),
  passenger_capacity: z.coerce
    .number()
    .min(1, 'Must have at least 1 passenger capacity'),
  luggage_capacity: z.coerce.number().min(0).optional().nullable(),
  year: z.coerce
    .number()
    .min(1900)
    .max(new Date().getFullYear() + 1)
    .optional()
    .nullable(),
  make: z.string().optional().nullable(),
  model: z.string().optional().nullable(),
  color: z.string().optional().nullable(),
  image_url: z.string().url().optional().nullable().or(z.literal('')),
  is_active: z.boolean().default(true),
  is_quote_only: z.boolean().default(false),
  // Mileage past which this vehicle's point-to-point trips are quoted by hand.
  // Null means never — there is no fleet-wide limit — so the field's onChange maps
  // a cleared input to null rather than to an empty string.
  quote_distance_miles: z.coerce
    .number()
    .min(1, 'Must be at least 1 mile')
    .max(5000, 'Must be 5000 miles or less')
    .nullable()
    .optional(),
  highlights: z.array(z.string()).optional().nullable(),
  limo_anywhere_vehicle_type_id: z.string().uuid().optional().nullable(),
})

export const vehicleFeatureSchema = z.object({
  name: z.string().min(1, 'Feature name is required'),
  icon: z.string().optional().nullable(),
})

export const vehiclePricingSchema = z.object({
  pricing_type: z.enum(PRICING_TYPES, {
    required_error: 'Pricing type is required',
  }),
  base_price: z.coerce.number().min(0, 'Base price must be 0 or greater'),
  price_per_hour: z.coerce.number().min(0).optional().nullable(),
  price_per_mile: z.coerce.number().min(0).optional().nullable(),
  minimum_hours: z.coerce.number().min(0).optional().nullable(),
  minimum_miles: z.coerce.number().min(0).optional().nullable(),
  service_fee: z.coerce.number().min(0).optional().nullable(),
  is_active: z.boolean().default(true),
  use_tiered_pricing: z.boolean().default(false),
})

export const vehiclePricingTierSchema = z.object({
  tier_order: z.coerce.number().min(1, 'Tier order must be at least 1'),
  miles_up_to: z.coerce
    .number()
    .min(0.1, 'Miles must be greater than 0')
    .optional()
    .nullable(),
  price_per_mile: z.coerce.number().min(0, 'Rate must be 0 or greater'),
})

export const vehicleAddonSchema = z.object({
  name: z.string().min(1, 'Add-on name is required'),
  description: z.string().optional().nullable(),
  price: z.coerce.number().min(0, 'Price must be 0 or greater'),
  price_type: z.enum(ADDON_PRICE_TYPES, {
    required_error: 'Price type is required',
  }),
  max_quantity: z.coerce.number().min(1, 'Must be at least 1').max(99, 'Maximum is 99').default(1),
  is_active: z.boolean().default(true),
})

export type VehicleFormData = z.infer<typeof vehicleSchema>
export type VehicleFeatureFormData = z.infer<typeof vehicleFeatureSchema>
export type VehiclePricingFormData = z.infer<typeof vehiclePricingSchema>
export type VehiclePricingTierFormData = z.infer<typeof vehiclePricingTierSchema>
export type VehicleAddonFormData = z.infer<typeof vehicleAddonSchema>

// Helper to generate slug from name
export function generateSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim()
}
